<!--
  Talk to ELSA — the conversation on an order (AB#21422).

  A third card on the Ask ELSA section, under the measured band and ELSA's
  read. Not a floating button and not a side sheet: the reader is already on
  the section where every answer's evidence lives, and a chat that covers the
  timeline it cites is a chat nobody can check.

  Three things make it more than a text box:

    - Every factual claim carries a [n] marker that is a chip. The chip names
      where the claim came from (a ticket, a timeline entry, a field) and
      opens the words it rests on. The backend strips markers that point at
      nothing, so a chip here always has something behind it.
    - ELSA drafts, and never sends. A draft is a box with the portal's own
      actions under it — copy, add as an internal comment, open the ticket —
      and each is a click the person makes.
    - The thread is shared. It belongs to the order, so a colleague's question
      from yesterday is there with their name on it, and the same thing is not
      asked three times.
-->
<template>
  <section class="talk" data-test="talk-elsa">
    <header class="talk-head">
      <button
        type="button"
        class="talk-collapse"
        :aria-expanded="!talkCollapsed"
        :aria-label="talkCollapsed ? 'Expand Talk to ELSA' : 'Collapse Talk to ELSA'"
        :title="talkCollapsed ? 'Expand' : 'Collapse'"
        data-test="talk-collapse"
        @click="toggleTalkCollapsed"
      >
        <ChevronDownIcon class="talk-collapse-icon" :class="{ 'talk-collapse-icon--collapsed': talkCollapsed }" aria-hidden="true" />
      </button>
      <AgentAvatar agent="elsa" :size="28" :state="isAsking ? 'thinking' : 'idle'" />
      <div class="talk-title">
        <h3>Talk to ELSA</h3>
        <span class="talk-sub">
          About order #{{ orderNo }} only · she reads the order, its timeline, comments, tickets and team chat
        </span>
      </div>
      <button
        v-if="!isEmpty"
        type="button"
        class="talk-clear"
        :disabled="isAsking"
        title="Clear this conversation for everyone"
        data-test="talk-clear"
        @click="clear"
      >
        Clear
      </button>
    </header>

    <div v-show="!talkCollapsed" class="talk-body" data-test="talk-body">
    <div v-if="!available" class="talk-notice" data-test="talk-unavailable">
      <strong>ELSA cannot answer right now.</strong> The read above and the figures are unaffected.
    </div>

    <template v-else>
      <div v-if="suggestions.length" class="talk-sugs" data-test="talk-suggestions">
        <button
          v-for="s in suggestions"
          :key="s"
          type="button"
          class="talk-sug"
          :disabled="isAsking"
          @click="askThis(s)"
        >
          {{ s }}
        </button>
      </div>

      <div ref="threadEl" class="talk-thread" :class="{ 'talk-thread--empty': isEmpty && !isLoading }">
        <div v-if="isLoading && isEmpty" class="talk-skeleton" aria-hidden="true"></div>

        <p v-else-if="isEmpty" class="talk-empty" data-test="talk-empty">
          Ask anything about this order. ELSA answers from what is on it and shows you where each
          answer comes from.
        </p>

        <div
          v-for="m in messages"
          :key="m.id"
          class="talk-msg"
          :class="{ 'talk-msg--me': m.role === 'user', 'talk-msg--elsa': m.role === 'elsa' }"
          :data-test="`talk-msg-${m.role}`"
        >
          <AgentAvatar v-if="m.role === 'elsa'" agent="elsa" :size="24" class="talk-face--sm" />
          <span v-else class="talk-who" :title="m.by">{{ initials(m.by) }}</span>

          <div class="talk-bubble">
            <div v-if="m.role === 'user'" class="talk-meta">
              {{ m.mine ? 'You' : m.by }} · {{ ago(m.at) }}
            </div>
            <p v-if="m.role === 'user'" class="talk-text">{{ m.text }}</p>

            <template v-else>
              <!-- The first line answers; the rest waits behind "Show more". -->
              <p
                v-for="(para, pi) in shownParagraphs(m)"
                :key="pi"
                class="talk-text"
                :class="{ 'talk-text--clamped': !isExpanded(m) && longFirst(m) }"
              >
                <template v-for="(part, k) in para" :key="k">
                  <button
                    v-if="part.n !== null"
                    type="button"
                    class="talk-cite"
                    :class="[`talk-cite--${part.evidence?.source ?? 'order'}`, { 'talk-cite--on': isOpen(m, part.n) }]"
                    :title="part.evidence?.quote"
                    :data-test="`talk-cite-${part.n}`"
                    @click="toggleCite(m, part.n)"
                  >
                    {{ part.evidence?.label ?? `[${part.n}]` }}
                  </button>
                  <template v-else>{{ part.text }}</template>
                </template>
              </p>

              <button
                v-if="canExpand(m)"
                type="button"
                class="talk-more"
                :aria-expanded="isExpanded(m)"
                data-test="talk-more"
                @click="toggleExpanded(m)"
              >
                {{ isExpanded(m) ? 'Show less' : 'Show more' }}
              </button>

              <div v-if="openCite(m)" class="talk-quote" data-test="talk-quote">
                <span class="talk-quote-src">{{ sourceName(openCite(m)!.source) }} · {{ openCite(m)!.label }}</span>
                <span class="talk-quote-text">“{{ openCite(m)!.quote }}”</span>
              </div>

              <div v-for="(d, di) in m.drafts" :key="di" class="talk-draft" :data-test="`talk-draft-${d.kind}`">
                <div class="talk-draft-lbl">{{ draftName(d.kind) }} · {{ d.title }}</div>
                <p class="talk-draft-text">{{ d.text }}</p>
                <div class="talk-tools">
                  <button
                    v-if="d.kind === 'customer_reply' || d.kind === 'factory_message'"
                    type="button"
                    class="talk-tool talk-tool--p"
                    @click="emit('act', 'support-threads', d.text)"
                  >
                    Open Order Communication
                  </button>
                  <button
                    type="button"
                    class="talk-tool"
                    :class="{ 'talk-tool--p': d.kind === 'internal_comment' }"
                    @click="emit('act', 'internal-comment', d.text)"
                  >
                    Add as internal comment
                  </button>
                  <button type="button" class="talk-tool" @click="copy(d.text, `${m.id}-${di}`)">
                    {{ copied === `${m.id}-${di}` ? 'Copied' : 'Copy' }}
                  </button>
                </div>
              </div>

              <div v-if="lookedAt(m)" class="talk-looked">Looked at: {{ lookedAt(m) }}</div>
              <div class="talk-meta talk-meta--elsa">ELSA · {{ ago(m.at) }}</div>
            </template>

            <div v-if="m.failed" class="talk-failed" data-test="talk-failed">
              {{ m.failed }}
              <button type="button" class="talk-retry" :disabled="isAsking" @click="askThis(m.text)">Try again</button>
            </div>
          </div>
        </div>

        <div v-if="isAsking" class="talk-msg talk-msg--elsa" data-test="talk-typing" aria-live="polite">
          <AgentAvatar agent="elsa" :size="24" state="thinking" class="talk-face--sm" />
          <div class="talk-bubble talk-bubble--typing">
            <span class="talk-dot"></span><span class="talk-dot"></span><span class="talk-dot"></span>
            <span class="talk-typing-text">Reading the order…</span>
          </div>
        </div>
      </div>

      <form class="talk-composer" @submit.prevent="submit">
        <textarea
          ref="inputEl"
          v-model="draft"
          class="talk-input"
          rows="1"
          :maxlength="MAX_QUESTION"
          placeholder="Ask ELSA about this order…"
          :disabled="isAsking"
          data-test="talk-input"
          @keydown.enter.exact.prevent="submit"
          @input="autosize"
        ></textarea>
        <button
          type="submit"
          class="talk-send"
          :disabled="isAsking || draft.trim() === ''"
          title="Ask (Enter)"
          data-test="talk-send"
        >
          <ElsaMark class="talk-send-mark" />
        </button>
      </form>

      <footer class="talk-foot">
        <span>Shared with everyone who can open this order</span>
        <span>ELSA can be wrong — check the evidence chips</span>
      </footer>
    </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import AgentAvatar from '@/components/Shared/AgentAvatar.vue';
import ElsaMark from '@/components/Dashboard/Order/components/ElsaMark.vue';
import { isCardCollapsed, setCardCollapsed } from '@/utils/elsaCardPrefs';
import {
  useOrderConversation,
  type ConversationMessage,
  type Evidence,
} from '@/composables/useOrderConversation';
import { splitAnswer, type AnswerPart } from '@/utils/elsaAnswer';

const props = defineProps<{ orderItemId: number; orderNo: string | number }>();

/**
 * `act` hands a draft to the portal's own actions. The text rides along so
 * the internal-note box opens with the draft in it rather than empty.
 */
const emit = defineEmits<{
  (e: 'act', action: 'internal-comment' | 'support-threads', text?: string): void;
}>();

// Collapse state, remembered per user. Open by default.
const storage = typeof window !== 'undefined' ? window.localStorage : null;
const talkCollapsed = ref(isCardCollapsed(storage, 'talk'));
const toggleTalkCollapsed = (): void => {
  talkCollapsed.value = !talkCollapsed.value;
  setCardCollapsed(storage, 'talk', talkCollapsed.value);
};

const MAX_QUESTION = 1000;

const conversation = useOrderConversation(props.orderItemId);
const messages = computed(() => conversation.messages.value);
const suggestions = computed(() => conversation.suggestions.value);
const available = computed(() => conversation.available.value);
const isLoading = computed(() => conversation.isLoading.value);
const isAsking = computed(() => conversation.isAsking.value);
const isEmpty = computed(() => conversation.isEmpty.value);

const draft = ref('');
const inputEl = ref<HTMLTextAreaElement | null>(null);
const threadEl = ref<HTMLElement | null>(null);
const copied = ref<string | null>(null);

onMounted(async () => {
  await conversation.load();
  scrollToEnd();
});

const scrollToEnd = async (): Promise<void> => {
  await nextTick();
  const el = threadEl.value;
  if (el) el.scrollTop = el.scrollHeight;
};

watch(() => messages.value.length, scrollToEnd);
watch(isAsking, (asking) => {
  if (asking) scrollToEnd();
});

const submit = async (): Promise<void> => {
  const text = draft.value.trim();
  if (text === '' || isAsking.value) return;

  draft.value = '';
  autosize();
  await conversation.ask(text);
  inputEl.value?.focus();
};

const askThis = (text: string): void => {
  if (isAsking.value) return;
  conversation.ask(text);
};

const clear = (): void => {
  conversation.clear();
};

const autosize = (): void => {
  const el = inputEl.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
};

const copy = async (text: string, key: string): Promise<void> => {
  try {
    await navigator.clipboard?.writeText(text);
    copied.value = key;
    setTimeout(() => {
      if (copied.value === key) copied.value = null;
    }, 1600);
  } catch {
    // The clipboard is a convenience; the text is on screen either way.
  }
};

/* ---- rendering an answer ---- */

const paragraphs = (m: ConversationMessage): AnswerPart[][] => splitAnswer(m.text, m.evidence ?? []);

// Long answers open on their first paragraph; "Show more" reveals the rest.
const LONG_FIRST_CHARS = 240;
const expanded = ref<Record<number, boolean>>({});
const isExpanded = (m: ConversationMessage): boolean => !!expanded.value[m.id];
const toggleExpanded = (m: ConversationMessage): void => {
  expanded.value = { ...expanded.value, [m.id]: !isExpanded(m) };
};
const paraLength = (para: AnswerPart[]): number =>
  para.reduce((n, p) => n + (p.n === null ? p.text.length : 4), 0);
const longFirst = (m: ConversationMessage): boolean =>
  paraLength(paragraphs(m)[0] ?? []) > LONG_FIRST_CHARS;
const canExpand = (m: ConversationMessage): boolean => paragraphs(m).length > 1 || longFirst(m);
const shownParagraphs = (m: ConversationMessage): AnswerPart[][] =>
  isExpanded(m) ? paragraphs(m) : paragraphs(m).slice(0, 1);

const openCites = ref<Record<number, number | null>>({});
const isOpen = (m: ConversationMessage, n: number): boolean => openCites.value[m.id] === n;
const toggleCite = (m: ConversationMessage, n: number): void => {
  openCites.value = { ...openCites.value, [m.id]: isOpen(m, n) ? null : n };
};
const openCite = (m: ConversationMessage): Evidence | null => {
  const n = openCites.value[m.id];
  if (n == null) return null;

  return (m.evidence ?? []).find((e) => e.n === n) ?? null;
};

const SOURCE_NAMES: Record<string, string> = {
  order: 'Order',
  timeline: 'Timeline',
  comment: 'Internal comment',
  ticket: 'Order Communication',
  invoice: 'Invoice',
  tracking: 'Tracking',
  related_order: 'Another order',
  read: "ELSA's read",
  measured: 'Measured',
};
const sourceName = (source: string): string => SOURCE_NAMES[source] ?? 'Order';

const DRAFT_NAMES: Record<string, string> = {
  customer_reply: 'Draft reply to customer',
  internal_comment: 'Draft internal comment',
  factory_message: 'Draft message to factory',
};
const draftName = (kind: string): string => DRAFT_NAMES[kind] ?? 'Draft';

const LOOKED_NAMES: Record<string, [string, string]> = {
  timeline_events: ['timeline move', 'timeline moves'],
  internal_comments: ['comment', 'comments'],
  support_threads: ['ticket', 'tickets'],
  // AB#21728 — General Comments, internal notes and the order comment feed.
  order_notes: ['order note', 'order notes'],
  ticket_messages: ['ticket message', 'ticket messages'],
  products: ['product', 'products'],
  invoices: ['invoice', 'invoices'],
  tracking_events: ['tracking event', 'tracking events'],
  related_orders: ['other order for this customer', 'other orders for this customer'],
};

const lookedAt = (m: ConversationMessage): string =>
  Object.entries(m.looked_at ?? {})
    .filter(([, n]) => n > 0)
    .map(([k, n]) => {
      const names = LOOKED_NAMES[k];
      if (!names) return `${n} ${k.replace(/_/g, ' ')}`;

      return `${n} ${n === 1 ? names[0] : names[1]}`;
    })
    .join(' · ');

const initials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (name === 'You') return 'You';

  return words
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
};

/** Coarse, rounds down — the same helper as the read card, kept local. */
const ago = (iso: string | null): string => {
  if (!iso) return 'just now';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'recently';

  const minutes = Math.floor((Date.now() - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.floor(hours / 24);

  return `${days} day${days === 1 ? '' : 's'} ago`;
};
</script>

<style scoped>
/* px on purpose: the root font is 12.8px. Palette is the read card's: ink
   #16211e, mute #7d8a87, ELSA purple #7c5cd6 / #8b6ee0, brand teal (the accent token). */
.talk {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 0 0 3px rgba(124, 92, 214, 0.12);
  overflow: hidden;
}

.talk-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
  background: var(--surface-2);
}

.talk-collapse {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  flex: none;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--ink-mute);
  cursor: pointer;
}

.talk-collapse:hover {
  background: var(--surface-3);
  color: var(--ink);
}

.talk-collapse:focus-visible {
  outline: 2px solid #7c5cff;
  outline-offset: 1px;
}

.talk-collapse-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.15s ease;
}

.talk-collapse-icon--collapsed {
  transform: rotate(-90deg);
}

.talk-face {
  width: 26px;
  height: 26px;
  flex: none;
}

.talk-face--sm {
  width: 24px;
  height: 24px;
  margin-top: 2px;
}

.talk-title {
  min-width: 0;
  flex: 1;
}

.talk-title h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
}

.talk-sub {
  display: block;
  font-size: 11.5px;
  color: var(--ink-soft);
}

.talk-clear {
  background: none;
  border: 0;
  color: var(--ink-soft);
  font-size: 11.5px;
  cursor: pointer;
  padding: 4px 6px;
}

.talk-clear:hover:not(:disabled) {
  color: var(--danger);
}

.talk-notice {
  padding: 12px 16px;
  font-size: 12.5px;
  color: var(--ink-soft);
}

.talk-sugs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding: 10px 16px 0;
}

.talk-sug {
  font-size: 11.5px;
  border: 1px solid var(--border);
  background: var(--surface-2);
  color: #8b6ee0;
  border-radius: 20px;
  padding: 4px 11px;
  cursor: pointer;
  text-align: left;
}

.talk-sug:hover:not(:disabled) {
  background: var(--surface-3);
}

.talk-sug:disabled {
  opacity: 0.6;
  cursor: default;
}

.talk-thread {
  display: grid;
  gap: 14px;
  padding: 14px 16px;
  max-height: 520px;
  overflow-y: auto;
}

.talk-skeleton {
  height: 60px;
  border-radius: 8px;
  background: var(--surface-2);
}

.talk-empty {
  margin: 0;
  font-size: 12.5px;
  color: var(--ink-soft);
}

.talk-msg {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.talk-msg--me {
  flex-direction: row-reverse;
}

.talk-who {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 600;
  font-size: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  margin-top: 2px;
}

.talk-bubble {
  max-width: 80%;
  border-radius: 12px;
  padding: 9px 13px;
  font-size: 12.8px;
  min-width: 0;
}

.talk-msg--me .talk-bubble {
  background: var(--accent);
  color: #fff;
  border-bottom-right-radius: 4px;
}

.talk-msg--elsa .talk-bubble {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-bottom-left-radius: 4px;
  color: var(--ink);
}

.talk-meta {
  font-size: 10.5px;
  opacity: 0.75;
  margin-bottom: 2px;
}

.talk-meta--elsa {
  color: var(--ink-soft);
  opacity: 1;
  margin: 8px 0 0;
}

.talk-text {
  margin: 0 0 8px;
  white-space: pre-line;
  line-height: 1.5;
}

.talk-text:last-child {
  margin-bottom: 0;
}

.talk-cite {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  line-height: 1.3;
  border-radius: 5px;
  padding: 1px 6px;
  background: var(--surface);
  border: 1px solid var(--border);
  color: #8b6ee0;
  margin: 0 2px;
  vertical-align: middle;
  white-space: nowrap;
  cursor: pointer;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.talk-cite::before {
  content: '';
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #7c5cd6;
  flex: none;
}

.talk-cite--ticket::before {
  background: #1f73b7;
}

.talk-cite--timeline::before,
.talk-cite--measured::before {
  background: #a8690f;
}

.talk-cite--comment::before {
  background: #7d8a87;
}

.talk-cite--invoice::before,
.talk-cite--tracking::before {
  background: #1e8e5a;
}

.talk-cite--on,
.talk-cite:hover {
  background: var(--surface-3);
}

.talk-quote {
  margin: 6px 0 8px;
  padding: 8px 10px;
  border-left: 3px solid #7c5cd6;
  background: var(--surface);
  border-radius: 0 6px 6px 0;
  display: grid;
  gap: 2px;
}

.talk-quote-src {
  font-size: 10.5px;
  color: #8b6ee0;
  font-weight: 600;
}

.talk-quote-text {
  font-size: 12px;
  color: var(--ink);
  white-space: pre-line;
}

.talk-draft {
  margin-top: 10px;
  border: 1px dashed var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--surface);
}

.talk-draft-lbl {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8b6ee0;
  font-weight: 600;
  margin-bottom: 4px;
}

.talk-draft-text {
  margin: 0 0 8px;
  font-size: 12.3px;
  color: var(--ink);
  white-space: pre-line;
}

.talk-tools {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.talk-tool {
  font-size: 11.5px;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 4px 10px;
  background: var(--surface);
  color: var(--ink);
  cursor: pointer;
}

.talk-tool:hover {
  background: var(--surface-2);
}

.talk-tool--p,
.talk-tool--p:hover {
  background: var(--accent);
  color: #fff;
  border-color: var(--accent);
}

.talk-looked {
  font-size: 10.5px;
  color: var(--ink-soft);
  margin-top: 8px;
}

.talk-failed {
  margin-top: 6px;
  font-size: 11.5px;
  color: #ffe1de;
}

.talk-retry {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.6);
  color: #fff;
  border-radius: 5px;
  font-size: 11px;
  padding: 1px 7px;
  margin-left: 6px;
  cursor: pointer;
}

.talk-bubble--typing {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.talk-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #7c5cd6;
  opacity: 0.5;
  animation: talk-blink 1.2s infinite;
}

.talk-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.talk-dot:nth-child(3) {
  animation-delay: 0.4s;
}

.talk-typing-text {
  margin-left: 6px;
  font-size: 11.5px;
  color: var(--ink-soft);
}

@keyframes talk-blink {
  0%,
  80%,
  100% {
    opacity: 0.3;
  }
  40% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .talk-dot {
    animation: none;
  }
}

.talk-composer {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding: 10px 16px 10px;
  border-top: 1px solid var(--border);
}

.talk-input {
  flex: 1;
  border: 1px solid var(--border);
  border-radius: 10px;
  min-height: 40px;
  max-height: 120px;
  padding: 10px 12px;
  font: inherit;
  font-size: 12.8px;
  color: var(--ink);
  resize: none;
  outline: none;
  background: var(--surface);
}

.talk-input:focus {
  border-color: #7c5cd6;
  box-shadow: 0 0 0 3px var(--surface-3);
}

.talk-input:disabled {
  background: var(--surface-2);
}

.talk-send {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 0;
  background: #7c5cd6;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: none;
}

.talk-send:disabled {
  opacity: 0.45;
  cursor: default;
}

.talk-send-mark {
  width: 18px;
  height: 18px;
}

.talk-foot {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 0 16px 10px;
  font-size: 10.5px;
  color: var(--ink-soft);
}

@media (max-width: 640px) {
  .talk-bubble {
    max-width: 92%;
  }

  .talk-thread {
    max-height: 420px;
  }
}

.talk-text--clamped {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.talk-more {
  margin: 2px 0 6px;
  padding: 2px 0;
  border: 0;
  background: none;
  color: var(--accent);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.talk-more:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: 4px;
}
</style>
